import AudienceEditor, { LoadingAudience } from '../editor';
import CustomAudienceEditor from '../../customAudiences/editor';
import NotFound from 'components/NotFound';
import { CombineAudience as CombineAudienceEditor } from '../combineAudience';

import axios from 'axios';
import { ENUM_SYSTEM_USED, KDSPageNames, Routes } from 'constants/index';
import { createContext, useEffect, useMemo, useState } from 'react';
import { useAppSelector } from 'redux/hooks';
import { useHistory, useLocation, useParams } from 'react-router';

export const AudienceContext = createContext({
  groupId: '',
  audienceType: '',
  lalModelType: '',
});

const API_ADDRESS = `${process.env.API_BASE_ADDRESS}/audience-studio/audiences`;

//Component to navigate standard/blander/file
const AudienceEditorRoute = () => {
  const [isLoading, setIsLoading] = useState(true);

  const { lalModelType } = useAppSelector(
    (state) => state.audienceReducer?.currentAudience || {}
  );
  const { id: groupId } = useAppSelector(
    (state) => state.audienceReducer?.currentGroup || {}
  );

  const history = useHistory();
  const location = useLocation();
  const params = useParams();
  const [audienceType, setAudienceType] = useState(
    location?.state?.audienceType || ''
  );

  // Redirect to default route if no params & group ID exists
  if (!params?.audienceid && !groupId) {
    history.push(`${Routes.AUDIENCE_STUDIO}?groupId=all&email=all`);
    return;
  }

  useEffect(async () => {
    if (!audienceType && params.audienceid) {
      await fetchAudienceTypeByAudienceId(params.audienceid);
    } else {
      setIsLoading(false);
    }

    return () => {
      setAudienceType('');
    };
  }, [audienceType]);

  // API call to get audience type by ID
  const fetchAudienceTypeByAudienceId = async (audienceId) => {
    const response = await axios.get(
      `${API_ADDRESS}/get-audience-type/${audienceId}`
    );
    if (response.data) {
      setAudienceType(response.data?.type);
    }
    setIsLoading(false);
  };

  const wrapperValue = {
    groupId,
    audienceType,
    lalModelType,
  };

  if (isLoading) {
    return <LoadingAudience />;
  }

  // Render specific editor based on the audience type
  switch (audienceType) {
    case ENUM_SYSTEM_USED.STANDARD_AUDIENCE:
      return (
        <AudienceWrapper value={wrapperValue}>
          <AudienceEditor />
        </AudienceWrapper>
      );
    case ENUM_SYSTEM_USED.COMBINED_AUDIENCE:
      return (
        <AudienceWrapper value={wrapperValue}>
          <CombineAudienceEditor />
        </AudienceWrapper>
      );
    case ENUM_SYSTEM_USED.CUSTOM_AUDIENCE:
      return (
        <AudienceWrapper value={wrapperValue}>
          <CustomAudienceEditor />
        </AudienceWrapper>
      );
    default:
      return <NotFound />;
  }
};

const AudienceWrapper = ({ value, children }) => {
  return (
    <AudienceContext.Provider value={value}>
      {children}
    </AudienceContext.Provider>
  );
};

// Component to redirect old audience IDs to their latest versions
export const AudienceRedirectRoute = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [audience, setAudience] = useState(null);
  const [isError, setIsError] = useState(false);

  const { audienceid } = useParams();
  const location = useLocation();
  const history = useHistory();
  const params = new URLSearchParams(location.search);
  const groupId = params.get('groupId') || 'all';
  const email = params.get('email') || 'all';

  // Fetch new audience ID if provided via old audienceid
  useEffect(async () => {
    if (audienceid) {
      await fetchNewAudienceByOldAudienceId(audienceid);
    } else {
      if (location.pathname.includes(Routes.AUDIENCE_MANAGER)) {
        history.push(`${Routes.AUDIENCE_STUDIO}${location.search}`);
        return;
      } else if (location.pathname.includes(Routes.CUSTOM_AUDIENCE_MANAGER)) {
        if (groupId !== 'all') {
          const result = await fetchNewGroupIdyOldGroupId(groupId);
          if (result?.newGroupId) {
            history.push(
              `${Routes.AUDIENCE_STUDIO}?groupId=${result?.newGroupId}&email=${email}`
            );
            return;
          }
        } else {
          history.push(`${Routes.AUDIENCE_STUDIO}${location.search}`);
          return;
        }
      }

      setIsLoading(false);
    }

    return () => {
      setAudience(null);
      setIsLoading(true);
    };
  }, [audienceid]);

  const audienceType = useMemo(() => {
    if (location.pathname.includes(Routes.AUDIENCE_MANAGER)) {
      return ENUM_SYSTEM_USED.STANDARD_AUDIENCE;
    } else if (location.pathname.includes(KDSPageNames.CUSTOMAUDIENCES)) {
      return ENUM_SYSTEM_USED.CUSTOM_AUDIENCE;
    }
    return null;
  }, []);

  // API call to get latest audience by old ID and type
  const fetchNewAudienceByOldAudienceId = async (id) => {
    try {
      const response = await axios.get(
        `${API_ADDRESS}/latest-audience-id/${id}?audience_type=${audienceType}`
      );
      if (response.data) {
        setAudience(response.data);
      }
    } catch (err) {
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  };

  // API call to get latest group id by old ID
  const fetchNewGroupIdyOldGroupId = async (id) => {
    try {
      const response = await axios.get(
        `${API_ADDRESS}/latest-custom-audience-group/${id}?audience_type=${audienceType}`
      );
      return response.data;
    } catch (err) {
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return <LoadingAudience />;
  }

  if (audience !== null) {
    history.push({
      pathname: `${Routes.AUDIENCE_STUDIO_EDITOR}/${audience.audience_id}`,
      state: {
        audienceType,
      },
    });
  }

  if (isError) {
    return <NotFound />;
  }

  return null;
};

export default AudienceEditorRoute;
